import { Alert, AlertTitle, Box, Typography } from "@mui/material";
import { checkLabels } from "./semanticReviewModel";

interface MergeDecision {
  status: "passed" | "failed" | "missing";
  reason: string;
  needsEvidenceReview?: boolean;
  unsupportedClaims?: Array<{ text: string }>;
}
export interface MergeCheck {
  status: "passed" | "blocked";
  reason: string;
  answerSource?: "existing" | "collected";
  answerChecks: Record<string, MergeDecision>;
  questionChecks: Array<
    MergeDecision & {
      question: string;
      origin: "existing" | "collected" | "generated";
    }
  >;
}

export function SemanticMergeDiagnostics({ check }: { check: MergeCheck }) {
  const questions = check.questionChecks.filter(
    (item) => item.status !== "passed",
  );
  const answers = Object.entries(check.answerChecks).filter(
    ([, item]) => item.status !== "passed",
  );
  if (check.status === "passed" && !questions.length) return null;
  return (
    <Box component="section" aria-label="Automatic merge checks" sx={{ mb: 2 }}>
      <Alert severity={check.status === "blocked" ? "warning" : "info"}>
        <AlertTitle>
          {check.status === "blocked"
            ? "Answers are equivalent, but automatic merging was blocked"
            : "Some generated alternatives were excluded"}
        </AlertTitle>
        {check.reason}
      </Alert>
      {answers.map(([key, item]) => (
        <Alert
          key={key}
          severity={
            item.needsEvidenceReview || item.status === "missing"
              ? "warning"
              : "error"
          }
          sx={{ mt: 1 }}
        >
          <Typography variant="body2" fontWeight={600}>
            {check.answerSource === "collected" ? "Collected" : "Existing"}{" "}
            answer recheck: {checkLabels[key] || key}
          </Typography>
          {item.reason}
          {item.unsupportedClaims?.map((claim) => (
            <Typography key={claim.text} variant="body2" sx={{ mt: 0.5 }}>
              Disputed words: “{claim.text}”
            </Typography>
          ))}
        </Alert>
      ))}
      {questions.map((item) => (
        <Box
          key={`${item.origin}:${item.question}`}
          sx={{
            mt: 1,
            pl: 1.5,
            borderLeft: 3,
            borderColor:
              check.status === "passed" ? "info.main" : "warning.main",
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            {item.origin === "collected"
              ? "Collected question"
              : item.origin === "existing"
                ? "Existing phrasing"
                : "Generated alternative (excluded)"}
          </Typography>
          <Typography variant="body2">“{item.question}”</Typography>
          <Typography variant="body2">{item.reason}</Typography>
        </Box>
      ))}
    </Box>
  );
}
